
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2 } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ApiResponseDisplay } from './components/ApiResponseDisplay';

export const RenewalSystemTest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const handleRenewalTest = async () => {
    setIsLoading(true);
    setError(null);
    setResponse(null);

    try {
      // Call the subscription renewal edge function with force processing
      const { data, error } = await supabase.functions.invoke('subscription-renewal', {
        body: { 
          forceProcess: true,
          apiKey: 'test_mode' 
        }
      });

      if (error) {
        console.error('Renewal function error:', error);
        throw new Error(`Renewal system error: ${error.message}`);
      }

      setResponse(data);
      
      // Check if the response indicates success or failure
      const isSuccess = data?.status === 'success';
      
      toast({
        variant: isSuccess ? 'default' : 'destructive',
        title: isSuccess ? 'Renewal Test Completed' : 'Renewal Test Issues',
        description: data?.message || 'The subscription renewal test has completed.'
      });

      // If there were subscriptions with passed end dates that weren't processed automatically
      if (data?.post_renewal?.remaining_unprocessed > 0) {
        toast({
          variant: 'destructive',
          title: 'Warning: Unprocessed Subscriptions',
          description: `${data.post_renewal.remaining_unprocessed} subscriptions with passed end dates weren't processed automatically.`
        });
      }
      
      // Show specific message about transaction numbers if any manual processing was done
      if (data?.post_renewal?.manually_processed > 0) {
        toast({
          variant: 'default',
          title: 'Manual Processing Completed',
          description: `${data.post_renewal.manually_processed} subscriptions were manually processed with 6-character transaction numbers.`
        });
      }
    } catch (err: any) {
      console.error('Renewal test error:', err);
      
      // Extract more details from the error if possible
      let errorMessage = err.message || 'An unknown error occurred';
      
      // Try to parse JSON error messages that might be embedded in the error
      try {
        if (typeof errorMessage === 'string' && errorMessage.includes('{')) {
          const jsonStart = errorMessage.indexOf('{');
          const jsonEnd = errorMessage.lastIndexOf('}');
          if (jsonStart >= 0 && jsonEnd > jsonStart) {
            const jsonPart = errorMessage.substring(jsonStart, jsonEnd + 1);
            const errorObj = JSON.parse(jsonPart);
            if (errorObj.message) {
              errorMessage = `${errorMessage.substring(0, jsonStart).trim()}: ${errorObj.message}`;
            }
          }
        }
      } catch (parseErr) {
        // If JSON parsing fails, use the original error message
        console.log('Error parsing embedded JSON error:', parseErr);
      }
      
      setError(`Unexpected error in subscription renewal: ${errorMessage}`);
      toast({
        variant: 'destructive',
        title: 'Renewal Test Failed',
        description: 'Failed to run the renewal test. See details below.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Subscription Renewal System Test</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            This will force the subscription renewal process to run, processing any subscriptions 
            that have passed their end date.
          </p>
          <Button 
            onClick={handleRenewalTest} 
            disabled={isLoading}
            variant="default"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running Test...
              </>
            ) : 'Run Renewal Test'}
          </Button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-800 rounded-md overflow-auto">
            <p className="font-semibold">Error:</p>
            <pre className="text-xs mt-2 whitespace-pre-wrap">{error}</pre>
          </div>
        )}

        {response && (
          <div className="space-y-2">
            <div className={`p-3 rounded-md ${response.status === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <span className="font-semibold">Status:</span> {response.status === 'success' ? 'Successful' : 'Failed'}
            </div>
            <div className="p-3 bg-blue-50 text-blue-800 rounded-md">
              <p>{response.message}</p>
              <p className="text-xs mt-1">Timestamp: {response.timestamp}</p>
            </div>
            <ApiResponseDisplay response={response} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
